<script setup lang="ts">
import DOMPurify from 'dompurify'

import { onMounted, ref, watch } from 'vue'

import { useMarkdown } from '../../composables/markdown'

interface Props {
  content: string
  class?: string | string[]
}

const props = defineProps<Props>()

const processedContent = ref('')
const { process, processSync } = useMarkdown()

/**
 * 分离状态描述和对话
 * 状态描述:以小写字母开头或包含动作动词的动作描述
 * 对话:以大写字母、标点符号或引号开头的文本
 */
function separateActionsAndDialog(text: string): string {
  // 按行分割
  const lines = text.split('\n').filter(line => line.trim())
  const result: string[] = []

  for (const line of lines) {
    // 使用空格分割但保留空格分隔的词组
    const segments = line.split(/\s{2,}/).filter(s => s.trim())

    for (const segment of segments) {
      const trimmed = segment.trim()
      if (!trimmed)
        continue

      // 判断是否为状态描述
      const isAction = isActionDescription(trimmed)

      if (isAction) {
        // 状态描述用斜体标记
        result.push(`*${trimmed}*`)
      }
      else {
        // 对话正常显示
        result.push(trimmed)
      }
    }

    // 添加换行
    result.push('')
  }

  return result.join('\n').trim()
}

/**
 * 判断文本是否为状态描述
 */
function isActionDescription(text: string): boolean {
  const trimmed = text.trim()

  // 空文本不算
  if (!trimmed)
    return false

  // 如果已经用斜体包裹,跳过
  if (trimmed.startsWith('*') && trimmed.endsWith('*'))
    return false

  // 常见的状态描述模式
  const actionPatterns = [
    // 以小写字母开头(通常是动作描述)
    /^[a-z]/,
    // 包含常见动作动词
    /\b(eyes|reaches|takes|catches|clears|goes|turns|looks|smiles|frowns|sighs|shrugs|nods|shakes|wipes|gazes|breathes)\b/i,
    // 描述性语句(包含多个动词)
    /^(he|she|they|it)\s+\w+/i,
    // 包含 "against her will" 等描述性短语
    /\bagainst (his|her|their) will\b/i,
    // 包含 "muffled" 等声音描述
    /\b(muffled|whispered|shouted|muttered)\b/i,
  ]

  // 检查是否匹配动作模式
  for (const pattern of actionPatterns) {
    if (pattern.test(trimmed))
      return true
  }

  // 对话特征:以大写字母开头且包含对话标点
  const dialogPatterns = [
    // 以大写字母开头(通常是开始说话)
    /^[A-Z]/,
    // 以对话标点开头
    /^["'`]/,
    // 包含大量对话标点(感叹号、问号等)
    /[!?]{2,}/,
    // 以对话结尾
    /[!?]$/,
  ]

  for (const pattern of dialogPatterns) {
    if (pattern.test(trimmed))
      return false
  }

  // 默认判断:如果以小写字母开头,认为是动作描述
  return /^[a-z]/.test(trimmed)
}

async function processContent() {
  if (!props.content) {
    processedContent.value = ''
    return
  }

  try {
    // 先分离状态描述和对话
    const separatedContent = separateActionsAndDialog(props.content)
    // 然后处理markdown
    processedContent.value = DOMPurify.sanitize(await process(separatedContent))
  }
  catch (error) {
    console.warn('Failed to process markdown with syntax highlighting, using fallback:', error)
    const separatedContent = separateActionsAndDialog(props.content)
    processedContent.value = DOMPurify.sanitize(processSync(separatedContent))
  }
}

// Process content when it changes
watch(() => props.content, processContent, { immediate: true })

onMounted(() => {
  processContent()
})
</script>

<template>
  <div
    :class="props.class"
    class="markdown-content"
    v-html="processedContent"
  />
</template>

<style scoped>
.markdown-content :deep(pre) {
  overflow-x: auto;
  max-width: 100%;
  border-radius: 6px;
  padding: 1rem;
  margin: 0.5rem 0;
}

.markdown-content :deep(code) {
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 0.875em;
}

.markdown-content :deep(pre code) {
  display: block;
  width: fit-content;
  min-width: 100%;
}

/* Ensure horizontal scrolling for wide code blocks */
.markdown-content :deep(pre.shiki) {
  overflow-x: auto;
  white-space: pre;
}

.markdown-content :deep(.shiki) {
  border-radius: 6px;
  padding: 1rem;
  margin: 0.5rem 0;
}

/* Fallback styles for non-shiki code blocks */
.markdown-content :deep(pre:not(.shiki)) {
  background: #f6f8fa;
  border: 1px solid #d0d7de;
}

.dark .markdown-content :deep(pre:not(.shiki)) {
  background: #161b22;
  border: 1px solid #30363d;
}

/* Force Shiki multi-theme output to follow our dark mode */
.dark .markdown-content :deep(.shiki) {
  background: var(--shiki-dark-bg, #0d1117) !important;
  color: var(--shiki-dark, #e6edf3) !important;
}

.dark .markdown-content :deep(.shiki span[style*="--shiki-dark"]) {
  color: var(--shiki-dark, inherit) !important;
}

.dark .markdown-content :deep(.shiki span[style*="--shiki-dark-background"]) {
  background-color: var(--shiki-dark-background, var(--shiki-dark-bg, transparent)) !important;
}
</style>
